import { forwardRef } from "react";
import { CheckCircle2 } from "@semo/icons";
import type { ChatPaymentCardProps } from "../types";
import { cn, Skeleton } from "@semo/shared-ui";
import { ContractInfoDtoPaymentMethod } from "../types";

/**
 * PaymentCard - 결제 완료 알림 카드
 *
 * 결제가 정상적으로 완료되었음을 알리는 카드입니다.
 * 상세 보기와 영수증 출력 기능을 제공합니다.
 */
export const PaymentCard = forwardRef<HTMLDivElement, ChatPaymentCardProps>(
  (
    {
      message,
      partyName,
      amount,
      sender,
      paymentMethod,
      onViewDetail,
      onPrintReceipt,
      isPending,
    },
    ref,
  ) => {
    const { createdAt } = message;

    // Loading State
    if (isPending) {
      return (
        <div
          ref={ref}
          data-slot="chat-payment-card-skeleton"
          className={cn(
            "bg-white ",
            "border rounded-xl p-4 sm:p-5 shadow-sm w-full relative overflow-hidden",
            sender === "me" ? "border-green-500/30 " : "border-green-200 ",
          )}
        >
          {/* Header Skeleton */}
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 relative z-10">
            <Skeleton className="w-10 h-10 sm:w-12 sm:h-12 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>

          {/* Details Skeleton */}
          <div className="mb-2 sm:mb-3">
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>

          {/* Buttons Skeleton */}
          <div className="relative z-10 flex gap-2">
            <Skeleton className="flex-1 h-10 rounded-lg" />
            {onPrintReceipt && <Skeleton className="flex-1 h-10 rounded-lg" />}
          </div>
        </div>
      );
    }

    const time = new Date(createdAt).toLocaleString("ko-KR", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Format amount if it's a number
    const displayAmount =
      typeof amount === "number"
        ? `${amount.toLocaleString("ko-KR")}원`
        : amount;

    // Show receipt only for card payments (CARD) or when paymentMethod is not specified (backward compatibility)
    // Hide for CASH and DIRECT payments
    const shouldShowReceipt =
      onPrintReceipt &&
      paymentMethod !== ContractInfoDtoPaymentMethod.CASH &&
      paymentMethod !== ContractInfoDtoPaymentMethod.DIRECT;

    return (
      <div
        ref={ref}
        data-slot="chat-payment-card"
        className={cn(
          "bg-white ",
          "border rounded-xl p-4 sm:p-5 shadow-sm w-full relative overflow-hidden",
          sender === "me" ? "border-green-500/30 " : "border-green-200 ",
        )}
      >
        {/* Decorative Background */}
        <div className="absolute top-0 right-0 w-20 h-20 sm:w-24 sm:h-24 bg-green-50  rounded-bl-full -mr-8 -mt-8 pointer-events-none" />

        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 relative z-10">
          <div
            className={cn(
              "w-10 h-10 sm:w-12 sm:h-12 rounded-full",
              "bg-green-100 ",
              "flex items-center justify-center",
              "text-green-600 ",
              "shadow-sm shadow-green-500/10 shrink-0",
            )}
          >
            <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-base sm:text-base font-semibold text-slate-900 ">
              결제 완료
            </div>
            <div className="text-2xs sm:text-xs text-slate-500 ">
              {time}
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div
          className={cn(
            "relative z-10",
            "bg-green-50/50 ",
            "rounded-lg p-2.5 sm:p-3",
            "border border-green-100 ",
            "mb-2 sm:mb-3",
          )}
        >
          <div className="flex justify-between text-xs sm:text-sm mb-1">
            <span className="text-slate-500 ">결제 금액</span>
            <span className="font-semibold text-slate-900 ">{displayAmount}</span>
          </div>
          <div className="flex justify-between text-xs sm:text-sm">
            <span className="text-slate-500 ">거래 대상</span>
            <span className="font-medium text-slate-900  truncate ml-2">
              {partyName}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="relative z-10 flex gap-2">
          <button
            onClick={onViewDetail}
            className={cn(
              "flex-1 py-2",
              "bg-[#2563eb] hover:bg-primary-hover",
              "text-white",
              "text-xs sm:text-sm font-semibold rounded-lg",
              "shadow-sm",
              "transition-colors",
            )}
          >
            상세 보기
          </button>
          {shouldShowReceipt && (
            <button
              onClick={onPrintReceipt}
              className={cn(
                "flex-1 py-2",
                "bg-white ",
                "border border-slate-200 ",
                "text-slate-600 ",
                "text-xs sm:text-sm font-semibold rounded-lg",
                "hover:bg-slate-50 :bg-slate-600",
                "transition-colors",
              )}
            >
              영수증 출력
            </button>
          )}
        </div>
      </div>
    );
  },
);

PaymentCard.displayName = "ChatPaymentCard";
